import React, { useState } from 'react';
import {
  Accordion,
  Button,
  ButtonPriority,
  Checkbox,
  CheckboxGroup,
  HoverStyles,
  InputDialog,
  TextButton,
  RadioButtonGroup,
  RadioButton,
  Text,
} from 'wix-ui-tpa/cssVars';
import { Filter as FilterIcon } from '@wix/wix-ui-icons-common/on-stage';
import { useStyles } from '@wix/tpa-settings/react';
import { classes, st } from './FiltersModal.st.css';
import {
  countSelectedFilterOptions,
  getSelectedFilters,
  getSelectedOptionsFromList,
  getUpdatedFilters,
  isSameFiltersSelected,
} from '../utils';
import { useDailyAgendaActions } from '../../../../components/DailyAgenda/hooks/useDailyAgendaActions';
import { useTranslation } from '@wix/yoshi-flow-editor';
import { FilterModalDataHooks } from './const';
import { Filter, FilterOption, FilterType } from '../../../../types/types';
import { FiltersViewModel } from '../../../../components/DailyAgenda/viewModel/filtersViewModel/filtersViewModel';
import { TimeZoneViewModel } from '../../../../components/DailyAgenda/viewModel/timeZoneViewModel/timeZoneViewModel';
import stylesParams from '../../../../components/DailyAgenda/stylesParams';
import { WidgetComponents, WidgetElements } from '../../../../utils/bi/consts';

const TimezoneAccordionItemId = 'timezone-item';

export type FiltersModalProps = {
  filtersViewModel?: FiltersViewModel;
  timeZoneViewModel?: TimeZoneViewModel;
};

export const FiltersModal: React.FC<FiltersModalProps> = ({
  filtersViewModel,
  timeZoneViewModel,
}) => {
  const selectedTimezone = timeZoneViewModel?.options.find(
    (option) => option.selected,
  );
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [clonedFilters, setClonedFilters] = useState<Filter[]>([]);
  const [radioSelectedTimezone, setRadioSelectedTimezone] =
    useState<FilterOption | null>(selectedTimezone || null);
  const { onFiltersChange, onTimeZoneChange, onElementClicked } =
    useDailyAgendaActions();
  const { t } = useTranslation();
  const styles = useStyles();
  const headerTimezoneSelectionVisibility = styles.get(
    stylesParams.headerTimezoneSelectionVisibility,
  );
  const headerServiceFilterVisibility = styles.get(
    stylesParams.headerServiceFilterVisibility,
  );
  const headerLocationFilterVisibility = styles.get(
    stylesParams.headerLocationFilterVisibility,
  );
  const headerStaffFilterVisibility = styles.get(
    stylesParams.headerStaffFilterVisibility,
  );

  const filters = filtersViewModel?.filters ?? [];

  if (
    !filters ||
    (!headerStaffFilterVisibility &&
      !headerServiceFilterVisibility &&
      !headerLocationFilterVisibility &&
      !headerTimezoneSelectionVisibility)
  ) {
    return null;
  }

  const numberOfActiveFilters = countSelectedFilterOptions(filters);

  const shouldDisableClearButton =
    numberOfActiveFilters === 0 &&
    countSelectedFilterOptions(clonedFilters) === 0 &&
    radioSelectedTimezone?.id === selectedTimezone?.id;

  const openDialog = () => {
    setClonedFilters(filters);
    setIsDialogOpen(true);
    onElementClicked(
      WidgetComponents.FILTER,
      WidgetElements.FILTERS_ACCORDION_OPENED,
    );
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    onElementClicked(
      WidgetComponents.FILTER,
      WidgetElements.FILTERS_ACCORDION_CLOSED,
    );
    onElementClicked(
      WidgetComponents.FILTER,
      WidgetElements.FILTERS_ACCORDION_CLOSED,
    );
  };

  const applyFilters = () => {
    if (!isSameFiltersSelected(filters, clonedFilters)) {
      const newSelected = getSelectedFilters(clonedFilters);
      onFiltersChange(newSelected);
      onElementClicked(
        WidgetComponents.FILTER,
        WidgetElements.FILTERS_ACCORDION_APPLY,
      );
    }

    if (
      radioSelectedTimezone &&
      radioSelectedTimezone.id !== selectedTimezone?.id
    ) {
      onTimeZoneChange(radioSelectedTimezone.id);
    }
    setIsDialogOpen(false);
  };

  const clearFilters = () => {
    if (numberOfActiveFilters > 0) {
      onFiltersChange([]);
      onElementClicked(WidgetComponents.FILTER, WidgetElements.CLEAR_BUTTON);
    }
    setIsDialogOpen(false);
  };

  const getItemLabel = (label: string, selectedCount: number) =>
    selectedCount === 0
      ? label
      : t('filters.mobile.item.label.with-selection', { label, selectedCount });

  const toggleOptionHandler =
    (type: FilterType) => (optionToToggle: FilterOption) => {
      const toggledOption: FilterOption = {
        ...optionToToggle,
        selected: !optionToToggle.selected,
      };
      const updatedFilters = getUpdatedFilters(
        clonedFilters,
        type,
        toggledOption,
      );
      setClonedFilters(updatedFilters);
    };

  const ApplyButton = () => (
    <Button
      upgrade
      fullWidth
      className={classes.applyButton}
      data-hook={FilterModalDataHooks.APPLY_BUTTON}
      onClick={applyFilters}
      hoverStyle={HoverStyles.underline}
    >
      {t('filters.mobile.modal.primary.cta')}
    </Button>
  );

  const ClearButton = () => (
    <Button
      upgrade
      fullWidth
      priority={ButtonPriority.basicSecondary}
      className={classes.clearButton}
      data-hook={FilterModalDataHooks.CLEAR_BUTTON}
      onClick={clearFilters}
      disabled={shouldDisableClearButton}
      hoverStyle={HoverStyles.underline}
    >
      {t('filters.mobile.modal.secondary.cta')}
    </Button>
  );

  const OpenDialogButton = () => (
    <TextButton
      prefixIcon={<FilterIcon className={classes.filterModalCTASuffix} />}
      data-hook={FilterModalDataHooks.OPEN_BUTTON}
      onClick={openDialog}
      className={classes.openDialogButton}
    >
      {numberOfActiveFilters > 0 && (
        <span className={classes.appliedIndicator} />
      )}
    </TextButton>
  );

  return (
    <>
      <OpenDialogButton />
      <InputDialog
        title={t('daily-agenda.filters-modal.title')}
        data-hook={FilterModalDataHooks.DIALOG}
        className={st(classes.root, { open: isDialogOpen })}
        isOpen={isDialogOpen}
        onClose={closeDialog}
        fullscreen
        forceBWTheme
        customFooter={
          <div className={classes.dialogFooter}>
            <ClearButton />
            <ApplyButton />
          </div>
        }
      >
        <Accordion
          multiple
          data-hook={FilterModalDataHooks.ACCORDION}
          className={classes.accordion}
          initiallyExpanded={[
            TimezoneAccordionItemId,
            ...filters.map(({ type }) => type),
          ]}
          onItemClick={(filterType) =>
            onElementClicked(
              WidgetComponents.FILTER,
              WidgetElements.FILTERS_ACCORDION_ITEM,
              { filterType },
            )
          }
        >
          {[
            ...(headerTimezoneSelectionVisibility
              ? [
                  <Accordion.Item
                    id={TimezoneAccordionItemId}
                    className={st(classes.accordionItem)}
                    key={TimezoneAccordionItemId}
                    title={timeZoneViewModel?.label || 'Timezone'}
                  >
                    <Timezone
                      timeZoneViewModel={timeZoneViewModel}
                      selectedTimezone={radioSelectedTimezone}
                      toggleOption={(selectedOption) => {
                        setRadioSelectedTimezone(selectedOption);
                      }}
                    />
                  </Accordion.Item>,
                ]
              : []),
            ...clonedFilters
              .filter(({ type }) => {
                switch (type) {
                  case FilterType.SERVICE:
                    return headerServiceFilterVisibility;
                  case FilterType.LOCATION:
                    return headerLocationFilterVisibility;
                  case FilterType.STAFF_MEMBER:
                    return headerStaffFilterVisibility;
                  default:
                    return false;
                }
              })
              .map((filter) => {
                const { type, label, options } = filter;
                const selectedCount =
                  getSelectedOptionsFromList(options).length;

                return (
                  <Accordion.Item
                    id={type}
                    className={st(classes.accordionItem, { filterType: type })}
                    key={type}
                    title={getItemLabel(label, selectedCount)}
                  >
                    <FilterContent
                      options={options}
                      toggleOption={toggleOptionHandler(type)}
                    />
                  </Accordion.Item>
                );
              }),
          ]}
        </Accordion>
      </InputDialog>
    </>
  );
};

const FilterContent: React.FC<{
  options: FilterOption[];
  toggleOption: (option: FilterOption) => void;
}> = ({ options, toggleOption }) => {
  return <CheckboxFilter toggleOption={toggleOption} options={options} />;
};

const Timezone: React.FC<{
  timeZoneViewModel?: TimeZoneViewModel;
  selectedTimezone?: FilterOption | null;
  toggleOption: (option: FilterOption) => void;
}> = ({ timeZoneViewModel, selectedTimezone, toggleOption }) => {
  if (!selectedTimezone || !selectedTimezone) {
    return null;
  }

  const options = timeZoneViewModel?.options ?? [];
  const showTimezoneAsText = options?.length === 1;

  if (showTimezoneAsText) {
    return (
      <Text
        data-hook={FilterModalDataHooks.TIMEZONE_TEXT}
        className={classes.textTimezone}
      >
        {options[0].value}
      </Text>
    );
  }

  return (
    <RadioButtonGroup
      className={classes.checkboxGroup}
      name="radioButtonTimezone"
      onChange={(id) => {
        const selectedOption = options.find((opt) => opt.id === id);
        if (selectedOption) {
          toggleOption(selectedOption);
        }
      }}
      value={selectedTimezone.id}
      data-hook={FilterModalDataHooks.TIMEZONE_RADIO_BUTTON}
    >
      {options.map((option) => (
        <RadioButton
          data-hook={FilterModalDataHooks.RADIO + option.id}
          className={classes.item}
          key={option.id}
          label={option.value}
          value={option.id}
          checked={option.id === selectedTimezone.id}
        />
      ))}
    </RadioButtonGroup>
  );
};
export const CheckboxFilter: React.FC<{
  toggleOption: (option: FilterOption) => void;
  options: FilterOption[];
}> = ({ toggleOption, options }) => (
  <CheckboxGroup
    className={classes.checkboxGroup}
    data-hook={FilterModalDataHooks.CHECKBOX_GROUP}
  >
    {options.map((option) => (
      <Checkbox
        key={option.id}
        data-hook={FilterModalDataHooks.CHECKBOX + option.value}
        className={st(classes.item)}
        onChange={() => toggleOption(option)}
        checked={option.selected}
        label={option.value}
      />
    ))}
  </CheckboxGroup>
);
